import type { NarrativeSessionSummary } from "../components/narrative/NarrativeSessionHistory";
import type { NarrativeTranscriptEntry } from "../components/narrative/NarrativeSessionWorkspace";
import type { SensemakingResponse } from "../components/narrative/sensemaking-types";
import type { ReasoningLevel } from "../components/chat/reasoning";
import { getBrowserApiBase } from "./browser-api-base";
import { getAccessToken } from "./supabase";
import {
  requireSession,
  type NarrativeSessionBundle,
  type RawSession,
  type RawSessionDetail,
  toSessionBundle,
  toSessionSummary,
} from "./narrative-session-mappers";
import type { ArticleDatawrapperAsset } from "../components/memorandums/assets/datawrapper-asset";

const API_BASE = getBrowserApiBase();

export type { NarrativeSessionBundle } from "./narrative-session-mappers";

export interface CreateNarrativeSessionPayload {
  query: string;
  catalystIds: string[];
  narrativeSlugs: string[];
  title: string;
  color: string;
  deskId?: string | null;
  deferArtifacts?: boolean;
  reasoningLevel?: ReasoningLevel;
}

export interface NarrativeSessionLinkPayload {
  url: string;
  title?: string | null;
  source?: string | null;
  summary?: string | null;
  imageUrl?: string | null;
  kind?: string | null;
}

export interface NarrativeSessionCatalystPayload {
  riskflowItemId: string;
  role?: string;
  conflictScore?: number | null;
  conflictLabel?: string | null;
}

interface OgPreviewResponse {
  url?: string;
  kind?: string;
  title?: string;
  description?: string;
  image?: string;
  siteName?: string;
}

export async function fetchNarrativeSessions(filters?: {
  deskId?: string | null;
}): Promise<NarrativeSessionSummary[]> {
  const params = new URLSearchParams();
  if (filters?.deskId) params.set("deskId", filters.deskId);
  const query = params.toString();
  const path = query
    ? `/api/narrative/sessions?${query}`
    : "/api/narrative/sessions";
  const data = await requestJson<{ sessions?: RawSession[] }>(path);
  return (data.sessions ?? []).map(toSessionSummary);
}

export async function createNarrativeSession(
  payload: CreateNarrativeSessionPayload,
): Promise<NarrativeSessionBundle> {
  const body: Record<string, unknown> = {
    query: payload.query,
    catalystIds: payload.catalystIds,
    title: payload.title,
    color: payload.color,
    deferArtifacts: payload.deferArtifacts,
    reasoningLevel: payload.reasoningLevel,
    tags: payload.narrativeSlugs.map((tag) => ({
      tag,
      confidence: 1,
      source: "human",
    })),
  };
  if (payload.deskId) body.deskId = payload.deskId;
  const data = await requestJson<{ session?: RawSessionDetail }>(
    "/api/narrative/sessions",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
  return toSessionBundle(requireSession(data.session));
}

export async function fetchNarrativeSession(
  id: string,
): Promise<NarrativeSessionBundle> {
  const data = await requestJson<{ session?: RawSessionDetail }>(
    `/api/narrative/sessions/${encodeURIComponent(id)}`,
  );
  const bundle = toSessionBundle(requireSession(data.session));
  if ((bundle.session.transcript?.length ?? 0) > 0) return bundle;
  const transcript = await fetchLatestNarrativeTranscript(id).catch(() => []);
  return transcript.length
    ? { ...bundle, session: { ...bundle.session, transcript } }
    : bundle;
}

export async function updateNarrativeSession(input: {
  id: string;
  title?: string;
  color?: string;
  status?: string;
  coverImageUrl?: string | null;
  coverImagePrompt?: string | null;
}): Promise<NarrativeSessionBundle> {
  const body: Record<string, unknown> = {};
  if (input.title !== undefined) body.title = input.title;
  if (input.color !== undefined) body.color = input.color;
  if (input.status !== undefined) body.status = input.status;
  if (input.coverImageUrl !== undefined)
    body.coverImageUrl = input.coverImageUrl;
  if (input.coverImagePrompt !== undefined)
    body.coverImagePrompt = input.coverImagePrompt;
  const data = await requestJson<{ session?: RawSessionDetail }>(
    `/api/narrative/sessions/${encodeURIComponent(input.id)}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
  return toSessionBundle(requireSession(data.session));
}

export async function deleteNarrativeSession(id: string): Promise<void> {
  await requestJson(`/api/narrative/sessions/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export async function replaceNarrativeSessionCatalysts(
  sessionId: string,
  catalystIds: string[],
  catalysts: NarrativeSessionCatalystPayload[] = [],
): Promise<void> {
  await requestJson(
    `/api/narrative/sessions/${encodeURIComponent(sessionId)}/catalysts`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ catalystIds, catalysts }),
    },
  );
}

export async function refineNarrativeSession(input: {
  sessionId: string;
  query: string;
  catalystIds: string[];
  orientation: string;
  renderMode: string;
  reasoningLevel?: ReasoningLevel;
}): Promise<NarrativeSessionBundle> {
  const response = await requestJson<SensemakingResponse>(
    "/api/narrative/sensemaking",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        query: input.query,
        attachedHeadlineIds: input.catalystIds,
        orientation: input.orientation,
        renderMode: input.renderMode,
        reasoningLevel: input.reasoningLevel,
      }),
    },
  );

  await Promise.all([
    addNarrativeSessionMessage(input.sessionId, input.query),
    saveNarrativeArtifact(input.sessionId, "flow", {
      anchorCatalysts: response.anchorCatalysts,
      relatedCatalysts: response.relatedCatalysts,
      narrativeGroups: response.narrativeGroups,
      mermaidSource: response.mermaidSource,
      synthesisSummary: response.synthesisSummary,
      forecast: response.forecast,
      generatedAt: response.generatedAt,
    }),
    saveNarrativeArtifact(input.sessionId, "timeline", {
      nodes: response.timelineNodes,
      edges: response.timelineEdges,
      generatedAt: response.generatedAt,
    }),
    saveNarrativeArtifact(input.sessionId, "docs", {
      title: `Narrative Brief: ${response.anchorCatalysts[0]?.headline ?? "Desk Session"}`,
      summary: response.synthesisSummary,
      forecast: response.forecast,
      generatedAt: response.generatedAt,
      links: [],
    }),
  ]);

  return fetchNarrativeSession(input.sessionId);
}

async function addNarrativeSessionMessage(
  sessionId: string,
  content: string,
): Promise<void> {
  if (!content.trim()) return;
  await requestJson(
    `/api/narrative/sessions/${encodeURIComponent(sessionId)}/messages`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        role: "user",
        content,
        metadata: { source: "workspace-composer" },
      }),
    },
  );
}

export async function saveNarrativeArtifact(
  sessionId: string,
  type: "flow" | "timeline" | "docs",
  payload: Record<string, unknown>,
): Promise<void> {
  await requestJson(
    `/api/narrative/sessions/${encodeURIComponent(sessionId)}/artifacts/${type}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ payload }),
    },
  );
}

export async function saveNarrativeDatawrapperFigures(
  sessionId: string,
  figures: ArticleDatawrapperAsset[],
): Promise<void> {
  await requestJson(
    `/api/narrative/sessions/${encodeURIComponent(sessionId)}/artifacts/docs/datawrapper-figures`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ figures }),
    },
  );
}

export async function enrichNarrativeSessionLink(
  inputUrl: string,
): Promise<NarrativeSessionLinkPayload> {
  const url = normalizeLinkUrl(inputUrl);
  let preview: OgPreviewResponse | null = null;
  try {
    preview = await requestJson<OgPreviewResponse>(
      `/api/preview/og?url=${encodeURIComponent(url)}`,
    );
  } catch {
    preview = null;
  }

  const normalizedUrl = preview?.url ?? url;
  const host = linkHost(normalizedUrl);
  const kind = inferLinkKind(normalizedUrl, preview?.kind);
  const title = preview?.title?.trim() || titleFromUrl(normalizedUrl);
  const source = preview?.siteName?.trim() || host;
  return {
    url: normalizedUrl,
    title,
    source,
    summary: summarizeLinkPreview({ title, source, kind, preview }),
    imageUrl: preview?.image ?? null,
    kind,
  };
}

export async function addNarrativeSessionLinks(
  sessionId: string,
  links: NarrativeSessionLinkPayload[],
): Promise<NarrativeSessionLinkPayload[]> {
  const data = await requestJson<{ links?: NarrativeSessionLinkPayload[] }>(
    `/api/narrative/sessions/${encodeURIComponent(sessionId)}/links`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ links }),
    },
  );
  return data.links ?? links;
}

async function fetchLatestNarrativeTranscript(
  sessionId: string,
): Promise<NarrativeTranscriptEntry[]> {
  const threadData = await requestJson<{
    conversations?: Array<{ id?: unknown }>;
  }>(
    `/api/narrative/sessions/${encodeURIComponent(
      sessionId,
    )}/conversations?limit=1`,
  );
  const conversationId = String(threadData.conversations?.[0]?.id ?? "");
  if (!conversationId) return [];

  const detail = await requestJson<{ messages?: RawConversationMessage[] }>(
    `/api/narrative/sessions/${encodeURIComponent(
      sessionId,
    )}/conversations/${encodeURIComponent(conversationId)}`,
  );
  return (detail.messages ?? []).map(toTranscriptEntry).filter(Boolean);
}

interface RawConversationMessage {
  id?: unknown;
  role?: unknown;
  speaker?: unknown;
  content?: unknown;
  text?: unknown;
  createdAt?: unknown;
  created_at?: unknown;
  timestamp?: unknown;
}

function toTranscriptEntry(
  message: RawConversationMessage,
  index: number,
): NarrativeTranscriptEntry {
  return {
    id: String(message.id ?? `conversation-message-${index}`),
    speaker: String(message.speaker ?? message.role ?? "desk"),
    text: String(message.text ?? message.content ?? ""),
    timestamp: String(
      message.timestamp ?? message.createdAt ?? message.created_at ?? "",
    ),
  };
}

async function requestJson<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  const token = await getAccessToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  const response = await fetch(`${API_BASE}${path}`, { ...init, headers });
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(data?.error ?? `Narrative session ${response.status}`);
  }
  return data as T;
}

function normalizeLinkUrl(input: string): string {
  const trimmed = input.trim();
  const withScheme = /^https?:\/\//i.test(trimmed)
    ? trimmed
    : `https://${trimmed}`;
  const url = new URL(withScheme);
  if (url.protocol !== "http:" && url.protocol !== "https:") {
    throw new Error("Use an http or https URL.");
  }
  return url.toString();
}

function linkHost(value: string): string {
  try {
    return new URL(value).hostname.replace(/^www\./, "");
  } catch {
    return "web";
  }
}

function inferLinkKind(value: string, previewKind?: string): string {
  if (previewKind === "tweet" || previewKind === "youtube") return previewKind;
  const host = linkHost(value);
  if (
    /(^|\.)data\.gov$|(^|\.)kaggle\.com$|(^|\.)fred\.stlouisfed\.org$/i.test(
      host,
    )
  )
    return "dataset";
  if (/(^|\.)sec\.gov$|(^|\.)arxiv\.org$|(^|\.)github\.com$/i.test(host))
    return "research";
  return "article";
}

function titleFromUrl(value: string): string {
  try {
    const url = new URL(value);
    const words = url.pathname
      .split("/")
      .filter(Boolean)
      .slice(-2)
      .join(" ")
      .replace(/\.[a-z0-9]+$/i, "")
      .replace(/[_-]+/g, " ")
      .trim();
    return words || url.hostname.replace(/^www\./, "");
  } catch {
    return "Attached source";
  }
}

function summarizeLinkPreview(input: {
  title: string;
  source: string;
  kind: string;
  preview: OgPreviewResponse | null;
}): string {
  const description = input.preview?.description?.trim();
  if (description) return description.slice(0, 260);
  const label =
    input.kind === "dataset"
      ? "Dataset source"
      : input.kind === "research"
        ? "Research source"
        : input.kind === "tweet"
          ? "X post"
          : input.kind === "youtube"
            ? "Video source"
            : "Article source";
  return `${label} from ${input.source}: ${input.title}`.slice(0, 260);
}
